import {computed, ref, type Ref} from "vue";
import {useSearchQueryStore} from "@/stores/searchQuery.js";
import {lineInquiryClickEvent, pushDataLayerEvent, type LineInquirySurface} from "@/utils/inquiryEvents";

// 平台官方 LINE OA。dev/prod 不同 OA(各自接不同 bot/DB),由 env 帶入;未設時 fallback prod。
const LINE_OA_ID = import.meta.env.VITE_APP_LINE_OA_ID || '@768cpmhz'

/**
 * Batch C「用 LINE 詢問」的連結與桌面 QR 行為。
 *
 * 連結預填 `#S<id>` 短碼,bot(ai-support)以 `INQUIRY_CODE_RE` 解析 → 當場知道是哪一間場地。
 * **這個短碼是 bot 唯一的場地上下文來源**:少了它,租客在 OA 裡問「桌子這樣拼法是有50人嗎」
 * 這類問題就沒有可轉的對象,只能轉客服(見 work/plan/know-issues/場地問題轉不到場主_因為沒人知道是哪一間.md)。
 * 所以任何「看得到某一間場地」的頁面都該有這條連結,不只場地詳情頁。
 */
export function useLineInquiry(spaceId: Ref<number>) {
    const searchQueryStore = useSearchQueryStore()

    // AV P1(捕捉導向):把搜尋列已選的日期/人數/預算以「人類可讀」文字附到 LINE 訊息,
    // bot 仍只需解析 #S<id> 短碼(無須改動);只帶有選的部分。
    function buildInquiryDetailText(): string {
        const segments: string[] = []

        const rentTime = searchQueryStore.getRentTimeSelected
        if (rentTime?.begin_time) {
            const date = rentTime.begin_time.split(' ')[0].split('/').join('-')
            if (rentTime.rent_type === 1) {
                segments.push(`日期:${date}(日租)`)
            } else {
                const start = rentTime.begin_time.split(' ')[1]?.slice(0, 5) || ''
                const end = rentTime.end_time ? (rentTime.end_time.split(' ')[1]?.slice(0, 5) || '') : ''
                const hasSpecificTime = start && end && !(start === '00:00' && end === '23:59')
                segments.push(hasSpecificTime ? `日期:${date} ${start}~${end}` : `日期:${date}`)
            }
        }

        const people = searchQueryStore.getPeopleCapRange
        if (people && (people.min || people.max)) {
            segments.push(people.max >= 9999 ? `人數:${people.min}人以上` : `人數:${people.min}-${people.max}人`)
        }

        const price = searchQueryStore.getPriceRange
        if (price && (price.min || price.max)) {
            segments.push(price.max >= 999999 ? `預算:${price.min}以上` : `預算:${price.min}-${price.max}`)
        }

        return segments.join(' ')
    }

    const lineInquiryHref = computed(() => {
        const lines = [`我想詢問這個場地 #S${spaceId.value}`]
        const detail = buildInquiryDetailText()
        if (detail) {
            lines.push(detail)
        }
        return `https://line.me/R/oaMessage/${LINE_OA_ID}/?${encodeURIComponent(lines.join('\n'))}`
    })

    const lineQrVisible = ref(false)
    const lineQrDataUrl = ref('')

    function isMobileDevice(): boolean {
        return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    }

    // 桌面開啟「我們自己的」QR:LINE 桌面 app 不支援開啟 OA 對話框並預填(app-only),
    // 租客用手機 LINE 掃描 → 手機 app 開 OA 對話框、預填含 #S<id> 需求 → 送出即開始詢問。
    async function openLineQr() {
        const QRCode = (await import('qrcode')).default
        lineQrDataUrl.value = await QRCode.toDataURL(lineInquiryHref.value, {width: 220, margin: 1})
        lineQrVisible.value = true
    }

    // G14:LINE 這條路的點擊本來完全沒有量測 —— 漏斗少了一整條分支,而 submit_inquiry
    // 只看得到平台表單。這裡補上點擊事件,而且刻意是**另一個事件名**:
    // 按了 LINE 不等於送出需求,兩者混在一起會把點擊算成 lead。
    function trackLineInquiryClick(surface: LineInquirySurface) {
        pushDataLayerEvent(lineInquiryClickEvent(spaceId.value, surface))
    }

    // 主要 CTA <a> 點擊:手機讓 <a> 預設開 LINE app;桌面攔截改彈 QR。
    function onLineInquiryClick(e: MouseEvent) {
        if (isMobileDevice()) {
            trackLineInquiryClick('line_app')
            return
        }
        e.preventDefault()
        trackLineInquiryClick('desktop_qr')
        openLineQr()
    }

    // sticky 行動列(<div>,非 <a>)走 LINE 詢問:手機直接開通用連結帶起 LINE app;桌面(窄視窗)彈 QR。
    function goLineInquiry() {
        if (isMobileDevice()) {
            trackLineInquiryClick('line_app')
            window.open(lineInquiryHref.value, '_blank')
        } else {
            trackLineInquiryClick('desktop_qr')
            openLineQr()
        }
    }

    return {lineInquiryHref, lineQrVisible, lineQrDataUrl, openLineQr, onLineInquiryClick, goLineInquiry}
}
